// Internal state machines for the Builder-Promo routes.
// The create route uses real WTM generation when draft/task ids are present;
// otherwise it falls back to the prototype timer so old links still render.

import { creatingStages, updateStages, buildSteps } from './mock-data';

const realCreateStages = {
    draft: { eyebrow: 'STAGE 1', title: 'Understanding your business', percent: 15, active: 0, body: 0 },
    style_demo: { eyebrow: 'STAGE 2', title: 'Choosing a visual direction', percent: 35, active: 1, body: 1 },
    directions: { eyebrow: 'STAGE 3', title: 'Writing your homepage copy', percent: 58, active: 2, body: 2 },
    package: { eyebrow: 'STAGE 4', title: 'Designing your preview page', percent: 72, active: 3, body: 3 },
    images: { eyebrow: 'STAGE 5', title: 'Checking responsive details', percent: 88, active: 4, body: 4 },
    template: { eyebrow: 'STAGE 6', title: 'Finalizing your preview', percent: 96, active: 5, body: 4 },
    done: { eyebrow: 'READY', title: 'Your preview is ready', percent: 100, active: 6, body: 4 },
};

const clickThroughCreateSteps = ['draft', 'style_demo', 'directions', 'package', 'images', 'template', 'done'];

const finalBuildStages = {
    directions: { percent: 32, active: 1, message: 'Planning the full website package...' },
    package: { percent: 58, active: 2, message: 'Generating page copy, sections, and forms...' },
    images: { percent: 78, active: 3, message: 'Preparing images and visual assets...' },
    template: { percent: 92, active: 5, message: 'Saving your website draft...' },
    done: { percent: 100, active: 6, message: 'Your website draft is ready.' },
};
let generationConsoleInitialized = false;

const taskTypeToStep = {
    generate_draft: 'draft',
    generate_style_demo: 'style_demo',
    generate_directions: 'directions',
    generate_package: 'package',
    skip_pending_images: 'images',
    create_template: 'template',
};

// ---- /builder-promo/preview/create ----
export function initCreateStateMachine() {
    const $root = $('[data-bb-create]');
    if (!$root.length) return;

    const draftId = String($root.attr('data-bb-draft-id') || '').trim();
    const taskId = String($root.attr('data-bb-task-id') || '').trim();
    const statusUrl = String($root.attr('data-bb-status-url') || '').trim();
    const continueUrl = String($root.attr('data-bb-continue-url') || '').trim();
    const runUrl = String($root.attr('data-bb-run-url') || '').trim();

    if (isClickThroughMode()) {
        runBuilderPromoClickThroughFlow($root);
        return;
    }

    if (draftId && taskId && statusUrl && continueUrl) {
        runRealCreateFlow($root, createFlowConfig($root, { draftId, taskId, statusUrl, continueUrl, runUrl }));
        return;
    }

    if (draftId && runUrl && continueUrl) {
        hydrateRunFromDraft($root, createFlowConfig($root, { draftId, statusUrl, continueUrl, runUrl }));
        return;
    }

    runPrototypeCreateFlow($root);
}

export function runBuilderPromoCreateFlow($root, config) {
    runRealCreateFlow($root, createFlowConfig($root, config || {}));
}

export function runBuilderPromoClickThroughFlow($root) {
    initGenerationConsole();
    $root.data('bbClickThroughMode', true);
    $root.data('bbClickThroughIndex', 0);
    $root.find('[data-bb-progress-primary]').addClass('bb-hidden').prop('disabled', true);
    $root.find('[data-bb-final-action], [data-bb-when-finalized]').addClass('bb-hidden');
    $root.find('[data-bb-ai-review-panel]').addClass('bb-hidden');
    $root.find('[data-bb-ai-run-count]').text('0');
    $root.find('[data-bb-ai-run-card-wrap]').html('<div class="bb-ai-run-empty">Preview walkthrough mode: no generation run started.</div>');
    showClickThroughScaffold($root);
    renderCreateStage($root, realCreateStages.draft, 'Preview walkthrough mode: no backend generation has started.');
    updateClickThroughButton($root, 0);
}

function hydrateRunFromDraft($root, config) {
    initGenerationConsole();
    config = createFlowConfig($root, config || {});
    $root.data('bbGenerationConfig', config);
    renderCreateStage($root, realCreateStages.draft, 'Loading generation run...');
    refreshCreateDetails($root, config, 'draft', true);

    $.ajax({
        url: config.runUrl,
        type: 'POST',
        data: { draft_id: config.draftId },
        headers: ajaxHeaders(),
        success(response) {
            if (response && response.html) updateRunCard($root, response.html);
            const run = response && response.run ? response.run : null;
            if (!run) {
                showCreateError($root, 'Website generation run was not found.');
                return;
            }

            if (run.status === 'completed') {
                finalizeCreateFlow($root, run.created_entity || null);
                return;
            }

            if ((run.status === 'queued' || run.status === 'running') && run.active_task_id && config.statusUrl) {
                runRealCreateFlow($root, Object.assign({}, config, { taskId: run.active_task_id }));
                return;
            }

            renderCreateStage($root, stageForRun(run), run.last_message || 'Review the generation run.');
        },
        error(xhr) {
            showCreateError($root, errorMessage(xhr, 'Could not load website generation run.'));
        },
    });
}
function runPrototypeCreateFlow($root) {
    let i = 0;
    const advanceMs = 6000;

    function render(stage) {
        renderCreateStage($root, {
            eyebrow: stage.eyebrow,
            title: stage.title,
            percent: stage.percent,
            active: stage.active,
            body: i,
        });

        if (i >= creatingStages.length - 1) {
            finalizeCreateFlow($root, null);
        }
    }

    render(creatingStages[0]);

    const timer = setInterval(function () {
        i += 1;
        if (i >= creatingStages.length) {
            clearInterval(timer);
            return;
        }
        render(creatingStages[i]);
    }, advanceMs);
}

function runRealCreateFlow($root, config) {
    initGenerationConsole();
    config = createFlowConfig($root, config || {});
    $root.data('bbGenerationConfig', config);
    $root.data('bbClickThroughMode', false);
    $root.removeData('bbClickThroughIndex');
    updateClickThroughButton($root, 0);
    $root.find('.bb-openai-progress-grid--legacy').removeClass('bb-hidden').attr('aria-hidden', 'false');
    let currentTaskId = config.taskId;
    let currentStep = 'draft';
    let isAdvancing = false;
    refreshCreateDetails($root, config, currentStep, true);

    function poll() {
        if ($root.data('bbClickThroughMode')) return;
        $.ajax({
            url: config.statusUrl,
            type: 'POST',
            data: { task_id: currentTaskId },
            headers: ajaxHeaders(),
            success(response) {
                if ($root.data('bbClickThroughMode')) return;
                if (!response || !response.success || !response.task) {
                    showCreateError($root, 'Could not read website generation status.');
                    return;
                }

                const task = response.task;
                currentStep = stepFromTask(task) || currentStep;
                refreshCreateDetails($root, config, currentStep, false);
                renderCreateStage($root, stageForStep(currentStep), task.message || 'Working on your website...');

                if (task.status === 'failed' || task.status === 'canceled') {
                    showCreateError($root, task.error || task.message || 'Website generation stopped.');
                    return;
                }

                if (task.created_entity) {
                    finalizeCreateFlow($root, task.created_entity);
                    return;
                }

                if (task.status === 'completed' || task.status === 'awaiting_action') {
                    if (shouldStopForStyleDemoReview(task, currentStep)) {
                        stopForStyleDemoReview($root, config, task);
                        return;
                    }

                    if (currentStep === 'style_demo' && !hasRenderableStyleDemo(task)) {
                        setTimeout(poll, 2500);
                        return;
                    }

                    if (currentStep === 'template') {
                        finalizeCreateFlow($root, task.created_entity || null);
                        return;
                    }

                    if (!isAdvancing) {
                        isAdvancing = true;
                        continueGeneration($root, config, currentStep, function (nextTask) {
                            isAdvancing = false;
                            if (!nextTask) {
                                return;
                            }
                            currentTaskId = nextTask.id;
                            currentStep = stepFromTask(nextTask) || currentStep;
                            renderCreateStage($root, stageForStep(currentStep), nextTask.message || 'Starting next step...');
                            setTimeout(poll, 600);
                        });
                    }
                    return;
                }

                setTimeout(poll, 2500);
            },
            error(xhr) {
                showCreateError($root, errorMessage(xhr, 'Could not read website generation status.'));
            },
        });
    }

    renderCreateStage($root, stageForStep(currentStep));
    poll();
}

function showClickThroughScaffold($root) {
    $root.find('.bb-openai-progress-grid--legacy').removeClass('bb-hidden').attr('aria-hidden', 'false');
}

function clickThroughCreateStep($root) {
    if (!$root || !$root.length) return;

    $root.data('bbClickThroughMode', true);
    showClickThroughScaffold($root);
    $root.find('[data-bb-create-error]').addClass('bb-hidden').text('');
    $root.find('[data-bb-ai-review-panel]').addClass('bb-hidden');
    $root.find('[data-bb-final-action], [data-bb-when-finalized]').addClass('bb-hidden');
    $root.find('[data-bb-progress-primary]').addClass('bb-hidden').prop('disabled', true);
    $root.removeClass('is-finalized');

    const currentIndex = currentClickThroughIndex($root);
    if (currentIndex >= clickThroughCreateSteps.length - 1) {
        window.location.href = clickThroughUrl('/builder-promo/preview');
        return;
    }

    const nextIndex = currentIndex + 1;
    const step = clickThroughCreateSteps[nextIndex];
    const message = step === 'done'
        ? 'Preview walkthrough reached the final placeholder. No backend generation was advanced.'
        : 'Preview walkthrough mode: UI stage advanced locally.';

    $root.data('bbClickThroughIndex', nextIndex);
    renderCreateStage($root, realCreateStages[step], message);

    if (step === 'done') {
        $root.addClass('is-finalized');
        $root.find('[data-bb-final-link]').attr('href', clickThroughUrl('/builder-promo/preview'));
        $root.find('[data-bb-final-message]').text('Preview walkthrough reached the ready screen. No backend generation was advanced.');
        $root.find('[data-bb-final-action], [data-bb-when-finalized]').removeClass('bb-hidden');
    }

    updateClickThroughButton($root, nextIndex);
}

function currentClickThroughIndex($root) {
    const stored = Number($root.data('bbClickThroughIndex'));
    if (Number.isFinite(stored)) return stored;
    if ($root.hasClass('is-finalized')) return clickThroughCreateSteps.length - 1;

    const $steps = $root.find('[data-bb-step-item]');
    const activeIndex = $steps.index($steps.filter('.is-active').first());
    return activeIndex >= 0 ? activeIndex : 0;
}

function clickThroughStageIndex(stage) {
    return clickThroughCreateSteps.findIndex(function (key) {
        return realCreateStages[key] === stage;
    });
}

function updateClickThroughButton($root, index) {
    const $button = $root.find('[data-bb-preview-walkthrough-step]');
    if (!$button.length) return;
    $button.prop('disabled', false).text(index >= clickThroughCreateSteps.length - 1 ? 'Open preview-ready screen' : 'Next preview step');
}
function hasRenderableStyleDemo(task) {
    const run = task && task.run ? task.run : null;
    return Boolean(run && (run.demo_url || run.demo_page_id || run.has_demo_page));
}

function shouldStopForStyleDemoReview(task, currentStep) {
    const run = task && task.run ? task.run : null;
    const runStep = String(run && run.current_step ? run.current_step : '');
    return hasRenderableStyleDemo(task) && (currentStep === 'style_demo' || runStep === 'style_demo_review');
}

function stopForStyleDemoReview($root, config, task) {
    renderCreateStage(
        $root,
        realCreateStages.done,
        'Your style preview is ready. Open it to review the generated direction.'
    );

    $root.addClass('is-finalized');
    $root.find('[data-bb-create-error]').addClass('bb-hidden').text('');
    $root.find('[data-bb-progress-primary]').addClass('bb-hidden').prop('disabled', true);
    $root.find('[data-bb-final-link]').attr('href', '/builder-promo/preview');
    $root.find('[data-bb-final-message]').text('Your style preview is ready. Open it to review, request changes, or approve the direction.');
    $root.find('[data-bb-final-action], [data-bb-when-finalized]').removeClass('bb-hidden');
}

function continueGeneration($root, config, completedStep, done) {
    setStatusMessage($root, 'Starting the next website generation step...');

    $.ajax({
        url: config.continueUrl,
        type: 'POST',
        data: {
            draft_id: config.draftId,
            completed_step: completedStep,
        },
        headers: ajaxHeaders(),
        success(response) {
            if (!response || !response.success) {
                showCreateError($root, 'Could not continue website generation.');
                done(null);
                return;
            }


            if (response.done) {
                finalizeCreateFlow($root, response.created_entity || null);
                done(null);
                return;
            }

            if (!response.task || !response.task.id) {
                showCreateError($root, 'Website generation did not return the next task.');
                done(null);
                return;
            }

            done(response.task);
        },
        error(xhr) {
            showCreateError($root, errorMessage(xhr, 'Could not continue website generation.'));
            done(null);
        },
    });
}

function renderCreateStage($root, stage, message) {
    const safeStage = stage || realCreateStages.draft;
    const stageIndex = clickThroughStageIndex(safeStage);
    $root.find('[data-stage-eyebrow]').text(safeStage.eyebrow);
    $root.find('[data-stage-title]').text(safeStage.title);

    $root.find('[data-stage-body]').addClass('bb-hidden');
    $root.find('[data-stage-body="' + safeStage.body + '"]').removeClass('bb-hidden');

    const stageMessage = message || defaultStageMessage(safeStage);
    setProgress($root, safeStage.percent);
    setStatusMessage($root, stageMessage);
    $root.find('[data-bb-stage-snippet]').text(stageMessage);
    if (stageIndex !== -1 && !$root.data('bbClickThroughMode')) {
        $root.data('bbClickThroughIndex', stageIndex);
        updateClickThroughButton($root, stageIndex);
    }

    $root.find('[data-bb-step-item]').each(function (idx) {
        const $step = $(this);
        $step.removeClass('is-done is-active');
        if (idx < safeStage.active) $step.addClass('is-done');
        else if (idx === safeStage.active) $step.addClass('is-active');
    });
}

function refreshCreateDetails($root, config, stageKey, force) {
    if (!config || !config.draftUrl || !config.draftId) return;
    if (!force && $root.data('bbCreateDetailsLoading')) return;

    const nextKey = String(stageKey || 'draft');
    const lastKey = String($root.data('bbCreateDetailsStageKey') || '');
    const loadedAt = Number($root.data('bbCreateDetailsLoadedAt') || 0);
    if (!force && lastKey === nextKey && Date.now() - loadedAt < 9000) return;

    $root.data('bbCreateDetailsLoading', true);
    $.ajax({
        url: config.draftUrl,
        type: 'GET',
        data: { draft_id: config.draftId },
        headers: ajaxHeaders(),
        success(response) {
            if (response && response.success !== false) {
                applyCreateDetails($root, response);
                $root.data('bbCreateDetailsStageKey', nextKey);
                $root.data('bbCreateDetailsLoadedAt', Date.now());
            }
        },
        complete() {
            $root.data('bbCreateDetailsLoading', false);
        },
    });
}

function applyCreateDetails($root, response) {
    const draft = plainObject(response && response.draft);
    const run = plainObject(response && response.run);
    const artifacts = artifactMap(response, draft);
    const brief = plainObject(draft.brief);
    const brandStyle = plainObject(artifacts.brand_style);
    const colors = plainObject(brandStyle.colors);
    const typography = plainObject(brandStyle.typography);
    const demoPage = plainObject(artifacts.demo_page);
    const demoBrief = plainObject(artifacts.demo_page_brief);

    const businessName = firstText(
        brief.business_name,
        getPath(brandStyle, ['brand', 'company_name']),
        getPath(run, ['created_entity', 'name'])
    );
    const locations = cleanList(brief.locations, brief.service_area, getPath(brandStyle, ['brand', 'location']));
    const services = cleanList(brief.services);
    const personality = cleanList(
        getPath(brandStyle, ['art_direction', 'visual_keywords']),
        getPath(brandStyle, ['brand', 'brand_personality']),
        brief.style_keywords,
        brief.tone
    );

    setText($root, '[data-bb-stage-business]', businessName);
    // Fallbacks here are shown to shops and restaurants as well as trades: this
    // screen runs BEFORE the service/non-service verdict is stamped (F21).
    setText($root, '[data-bb-stage-location]', locations.length ? locations.slice(0, 2).join(' + ') : 'Your location');
    if (services.length) {
        setText($root, '[data-bb-stage-services]', services.slice(0, 3).join(', '));
    }

    if (personality.length) {
        setText($root, '[data-bb-brand-personality]', personality.slice(0, 6).join(', '));
    }
    renderBrandSwatches($root, colors);

    const headingFont = firstText(typography.heading_font, typography.subheading_font);
    const bodyFont = firstText(typography.body_font, typography.label_font);
    const fontSummary = [headingFont, bodyFont].filter(Boolean).join(' + ');
    setText($root, '[data-bb-brand-fonts]', fontSummary || 'Aa');

    const buttonBg = firstText(getPath(brandStyle, ['buttons', 'primary', 'bg_color']), colors.cta_primary_bg, colors.primary);
    const buttonText = firstText(getPath(brandStyle, ['buttons', 'primary', 'text_color']), colors.cta_primary_text, '#FFFFFF');
    const buttonRadius = firstText(getPath(brandStyle, ['buttons', 'primary', 'border_radius']), getPath(brandStyle, ['shapes', 'button_radius']));
    const $button = $root.find('[data-bb-brand-button]');
    if ($button.length) {
        if (isColor(buttonBg)) $button.css({ background: buttonBg, borderColor: buttonBg });
        if (isColor(buttonText)) $button.css({ color: buttonText });
        if (buttonRadius !== '') $button.css('border-radius', String(buttonRadius).match(/px$/) ? String(buttonRadius) : String(buttonRadius) + 'px');
        $button.text(buttonRadius !== '' ? 'Radius ' + buttonRadius : 'Primary CTA');
    }

    const heroHeadline = heroTitleFromArtifacts(artifacts, demoPage, demoBrief);
    if (heroHeadline) setText($root, '[data-bb-copy-quote]', quoteText(heroHeadline));

    const pageSummary = firstText(
        getPath(demoBrief, ['page_goal']),
        getPath(demoBrief, ['summary']),
        getPath(demoBrief, ['content_strategy']),
        brief.goal,
        getPath(brandStyle, ['brand_strategy', 'positioning'])
    );
    setText($root, '[data-bb-page-brief-summary]', pageSummary);

    const briefWords = cleanList(
        getPath(demoBrief, ['target_sections']),
        getPath(demoBrief, ['key_messages']),
        getPath(demoBrief, ['conversion_goals']),
        getPath(brandStyle, ['art_direction', 'visual_keywords'])
    );
    renderChips($root.find('[data-bb-page-brief-words]'), briefWords.slice(0, 5));

    const blocks = blockNamesFromDemoPage(demoPage);
    if (blocks.length) {
        setText($root, '[data-bb-layout-summary]', 'Designing ' + blocks.slice(0, 4).join(', ') + ' with responsive spacing.');
    }

    renderFinalChecklist($root, {
        brand: brandStyle,
        demoPage,
        blocks,
        run,
    });
}

function artifactMap(response, draft) {
    const map = {};
    const draftArtifacts = plainObject(draft && draft.artifacts);
    Object.keys(draftArtifacts).forEach(function (key) {
        map[key] = draftArtifacts[key];
    });
    const files = Array.isArray(response && response.artifacts) ? response.artifacts : [];
    files.forEach(function (artifact) {
        if (artifact && artifact.key) map[artifact.key] = artifact.payload;
    });
    return map;
}

function renderBrandSwatches($root, colors) {
    const palette = cleanList(
        colors.primary,
        colors.secondary,
        colors.accent,
        colors.neutral_dark,
        colors.neutral_light,
        colors.background_base
    ).filter(isColor).slice(0, 5);
    if (!palette.length) return;

    const $wrap = $root.find('[data-bb-brand-swatches]');
    if (!$wrap.length) return;
    $wrap.empty();
    palette.forEach(function (color) {
        $wrap.append($('<span class="bb-swatch"></span>').css('background', color).attr('title', color));
    });
}

function heroTitleFromArtifacts(artifacts, demoPage, demoBrief) {
    const pages = Array.isArray(artifacts.pages) ? artifacts.pages : [];
    return firstText(
        getPath(demoPage, ['page', 'blocks', 0, 'settings', 'hero_content', 'title_text']),
        getPath(demoPage, ['blocks', 0, 'settings', 'hero_content', 'title_text']),
        getPath(demoPage, ['settings', 'hero_content', 'title_text']),
        getPath(demoBrief, ['hero', 'headline']),
        getPath(demoBrief, ['hero_title']),
        getPath(demoBrief, ['headline']),
        getPath(pages, [0, 'page', 'blocks', 0, 'settings', 'hero_content', 'title_text']),
        getPath(pages, [0, 'blocks', 0, 'settings', 'hero_content', 'title_text'])
    );
}

function blockNamesFromDemoPage(demoPage) {
    let blocks = getPath(demoPage, ['page', 'blocks']);
    if (!Array.isArray(blocks)) blocks = getPath(demoPage, ['blocks']);
    if (!Array.isArray(blocks)) return [];
    return blocks.map(function (block) {
        return firstText(block.block_name, block.block_type, getPath(block, ['settings', 'type']));
    }).filter(Boolean);
}

function renderChips($wrap, values) {
    if (!$wrap.length || !values.length) return;
    $wrap.empty();
    values.forEach(function (value) {
        $wrap.append($('<span class="bb-create__chip"></span>').text(value));
    });
}

function renderFinalChecklist($root, details) {
    const hasBrand = Object.keys(plainObject(details.brand)).length > 0;
    const hasDemo = Object.keys(plainObject(details.demoPage)).length > 0;
    const items = [
        hasBrand ? 'Brand style prepared' : 'Brand direction prepared',
        hasDemo ? 'Homepage layout created' : 'Homepage layout prepared',
        'Page sections written',
        'Mobile preview checked',
        'Private preview saved',
    ];

    const $list = $root.find('[data-bb-final-checklist]');
    if (!$list.length) return;
    $list.empty();
    items.forEach(function (item) {
        $list.append('<li><span class="bb-checklist__mark" aria-hidden="true">&#10003;</span> ' + escapeHtml(item) + '</li>');
    });
}

function setText($root, selector, value) {
    const text = firstText(value);
    if (text === '') return;
    $root.find(selector).text(text);
}

function quoteText(value) {
    const text = firstText(value);
    if (!text) return '';
    return '"' + text.replace(/^"|"$/g, '') + '"';
}

function cleanList() {
    const values = [];
    Array.prototype.forEach.call(arguments, function (entry) {
        if (Array.isArray(entry)) {
            entry.forEach(function (item) {
                if (typeof item === 'string' && item.indexOf(',') !== -1) {
                    item.split(',').forEach(function (part) { values.push(part); });
                    return;
                }
                values.push(item);
            });
            return;
        }
        if (entry == null || entry === '') return;
        if (typeof entry === 'string' && entry.indexOf(',') !== -1) {
            entry.split(',').forEach(function (item) { values.push(item); });
            return;
        }
        values.push(entry);
    });

    const seen = {};
    return values.map(function (value) { return firstText(value); })
        .filter(Boolean)
        .filter(function (value) {
            const key = value.toLowerCase();
            if (seen[key]) return false;
            seen[key] = true;
            return true;
        });
}

function firstText() {
    for (let i = 0; i < arguments.length; i += 1) {
        const value = arguments[i];
        if (value == null) continue;
        if (Array.isArray(value)) {
            const list = cleanList(value);
            if (list.length) return list[0];
            continue;
        }
        if (typeof value === 'object') continue;
        const text = String(value).trim();
        if (text !== '') return text;
    }
    return '';
}

function plainObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function getPath(source, path) {
    let current = source;
    for (let i = 0; i < path.length; i += 1) {
        if (current == null) return null;
        current = current[path[i]];
    }
    return current;
}

function isColor(value) {
    return /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(String(value || '').trim());
}

function finalizeCreateFlow($root, createdEntity) {
    renderCreateStage($root, realCreateStages.done, 'Private preview saved.');
    $root.addClass('is-finalized');
    $root.find('[data-bb-create-error]').addClass('bb-hidden').text('');
    $root.find('[data-bb-progress-primary]').addClass('bb-hidden').prop('disabled', true);
    $root.find('[data-bb-final-action]').removeClass('bb-hidden');

    const $final = $root.find('[data-bb-when-finalized]');
    const $link = $root.find('[data-bb-final-link]');
    const $message = $root.find('[data-bb-final-message]');

    if (createdEntity && createdEntity.demo_url) {
        $link.attr('href', createdEntity.demo_url);
    }

    $message.text('We saved your private preview. You can review the design direction now.');

    $final.removeClass('bb-hidden');
}

function stageForStep(step) {
    return realCreateStages[step] || realCreateStages.draft;
}

function stageForRun(run) {
    const step = String(run && run.current_step ? run.current_step : 'draft');
    const mapped = {
        brief: 'draft',
        style_demo_review: 'style_demo',
        complexity: 'directions',
        generate_package: 'package',
        package_review: 'package',
        image_review: 'images',
        completed: 'done',
    }[step] || step;

    return stageForStep(mapped);
}

function stepFromTask(task) {
    return taskTypeToStep[String(task && task.type ? task.type : '')] || null;
}

function defaultStageMessage(stage) {
    if (stage === realCreateStages.draft) return 'Reading what you offer, your location, and your style notes.';
    if (stage === realCreateStages.style_demo) return 'Preparing the visual direction and style preview.';
    if (stage === realCreateStages.directions) return 'Drafting the homepage headline, proof points, and CTA copy.';
    if (stage === realCreateStages.package) return 'Designing the preview page layout and section rhythm.';
    if (stage === realCreateStages.images) return 'Checking responsive details and generated preview content.';
    if (stage === realCreateStages.template) return 'Saving the generated preview page for review.';
    if (stage === realCreateStages.done) return 'Your preview is ready.';
    return 'Working on your website...';
}

function setStatusMessage($root, message) {
    const $message = $root.find('[data-bb-status-message]');
    if ($message.length && message) $message.text(message);
}

function showCreateError($root, message) {
    setStatusMessage($root, 'Website generation needs attention.');
    $root.find('[data-bb-create-error]').removeClass('bb-hidden').text(message || 'Something went wrong. Please try again.');
}

function ajaxHeaders() {
    return {
        'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
        'Accept': 'application/json',
    };
}

function createFlowConfig($root, config) {
    return {
        draftId: config.draftId || String($root.attr('data-bb-draft-id') || '').trim(),
        taskId: config.taskId || String($root.attr('data-bb-task-id') || '').trim(),
        statusUrl: config.statusUrl || String($root.attr('data-bb-status-url') || '').trim(),
        continueUrl: config.continueUrl || String($root.attr('data-bb-continue-url') || '').trim(),
        runUrl: config.runUrl || String($root.attr('data-bb-run-url') || '').trim(),
        runDetailsUrl: config.runDetailsUrl || String($root.attr('data-bb-run-details-url') || '').trim(),
        draftUrl: config.draftUrl || String($root.attr('data-bb-draft-url') || '').trim(),
        deleteRunUrl: config.deleteRunUrl || String($root.attr('data-bb-delete-run-url') || '').trim(),
    };
}

function initGenerationConsole() {
    if (generationConsoleInitialized) return;
    generationConsoleInitialized = true;

    $(document).on('click', '[data-bb-preview-walkthrough-step]', function (e) {
        e.preventDefault();
        let $root = $(this).closest('[data-bb-create-progress], [data-bb-create]');
        if (!$root.length) $root = $('[data-bb-create]').first();
        clickThroughCreateStep($root);
    });

    $(document).on('click', '[data-bb-ai-run-details]', function (e) {
        e.preventDefault();
        const $root = $(this).closest('[data-bb-create-progress], [data-bb-create]');
        const config = createFlowConfig($root, $root.data('bbGenerationConfig') || {});
        const draftId = String($(this).attr('data-draft-id') || config.draftId || '').trim();
        openAiDetailsModal($root, config, draftId);
    });

    $(document).on('click', '[data-bb-ai-details-close]', function (e) {
        e.preventDefault();
        const $modal = $(this).closest('[data-bb-ai-details-modal]');
        $modal.addClass('bb-hidden').attr('aria-hidden', 'true');
    });

    $(document).on('click', '[data-bb-ai-run-continue]', function (e) {
        e.preventDefault();
        const $root = $(this).closest('[data-bb-create-progress], [data-bb-create]');
        const config = createFlowConfig($root, $root.data('bbGenerationConfig') || {});
        const draftId = String($(this).attr('data-draft-id') || config.draftId || '').trim();
        const continueStep = String($(this).attr('data-continue-step') || 'draft').trim();

        if (continueStep === 'style_demo') {
            openLatestStyleDemo($root, config, draftId);
            return;
        }

        openReviewPanel($root, config, draftId, continueStep);
    });

    $(document).on('click', '[data-bb-ai-run-more]', function (e) {
        e.preventDefault();
        const details = $(this).closest('[data-bb-ai-run-card]').find('.bb-ai-run-card__trace').get(0);
        if (details) details.open = !details.open;
    });

    $(document).on('click', '[data-bb-ai-run-delete]', function (e) {
        e.preventDefault();
        const $root = $(this).closest('[data-bb-create-progress], [data-bb-create]');
        const config = createFlowConfig($root, $root.data('bbGenerationConfig') || {});
        const draftId = String($(this).attr('data-draft-id') || config.draftId || '').trim();
        if (!draftId || !config.deleteRunUrl) return;
        if (!window.confirm('Delete this website generation run?')) return;

        $.ajax({
            url: config.deleteRunUrl,
            type: 'POST',
            data: { draft_id: draftId },
            headers: ajaxHeaders(),
            success() {
                $root.find('[data-bb-ai-run-card-wrap]').html('<div class="bb-ai-run-empty">Run deleted.</div>');
                $root.find('[data-bb-ai-review-panel]').addClass('bb-hidden');
            },
            error(xhr) {
                showCreateError($root, errorMessage(xhr, 'Could not delete this run.'));
            },
        });
    });

    $(document).on('click', '[data-bb-review-back]', function (e) {
        e.preventDefault();
        $(this).closest('[data-bb-ai-review-panel]').addClass('bb-hidden');
    });

    $(document).on('change', '[data-bb-artifact-select]', function () {
        renderSelectedArtifact($(this).closest('[data-bb-ai-review-panel]'));
    });

    $(document).on('click', '[data-bb-copy-artifact]', function (e) {
        e.preventDefault();
        const text = String($(this).closest('[data-bb-ai-review-panel]').find('[data-bb-artifact-preview]').text() || '');
        copyText(text);
    });

    $(document).on('click', '[data-bb-download-artifact]', function (e) {
        e.preventDefault();
        const $panel = $(this).closest('[data-bb-ai-review-panel]');
        const selected = selectedArtifact($panel);
        if (!selected) return;
        downloadJson(selected.filename || 'artifact.json', selected.payload);
    });

    $(document).on('click', '[data-bb-download-all-artifacts]', function (e) {
        e.preventDefault();
        const $panel = $(this).closest('[data-bb-ai-review-panel]');
        const artifacts = $panel.data('artifacts') || [];
        downloadJson('all-artifacts.json', artifacts);
    });

    $(document).on('click', '[data-bb-review-continue]', function (e) {
        e.preventDefault();
        const $panel = $(this).closest('[data-bb-ai-review-panel]');
        const $root = $panel.closest('[data-bb-create-progress], [data-bb-create]');
        const config = createFlowConfig($root, $root.data('bbGenerationConfig') || {});
        const draftId = String($panel.data('draftId') || config.draftId || '').trim();
        const continueStep = String($panel.data('continueStep') || 'draft').trim();
        if (!draftId || !config.continueUrl) return;

        const $button = $(this).prop('disabled', true).text('Continuing...');
        $.ajax({
            url: config.continueUrl,
            type: 'POST',
            data: { draft_id: draftId, completed_step: continueStep },
            headers: ajaxHeaders(),
            success(response) {
                if (response.run_card_html) updateRunCard($root, response.run_card_html);
                $panel.addClass('bb-hidden');
                if (response.done) {
                    finalizeCreateFlow($root, response.created_entity || null);
                    return;
                }
                if (response.task && response.task.id) {
                    runRealCreateFlow($root, createFlowConfig($root, Object.assign({}, config, {
                        draftId,
                        taskId: response.task.id,
                    })));
                    return;
                }
                refreshRunConsole($root, config);
            },
            error(xhr) {
                showCreateError($root, errorMessage(xhr, 'Could not continue website generation.'));
            },
            complete() {
                $button.prop('disabled', false).text(reviewContinueLabel(continueStep));
            },
        });
    });

    $(document).on('click', '.wtm-ai-artifact-copy-btn', function (e) {
        e.preventDefault();
        const source = $(this).closest('details').find('.wtm-ai-artifact-copy-source').first().val() || '';
        copyText(String(source));
    });
}

function updateRunCard($root, html) {
    if (!html) return;
    $root.find('[data-bb-ai-run-card-wrap]').html(html);
    $root.find('[data-bb-ai-run-count]').text('1');
}

function refreshRunConsole($root, config) {
    if (!config || !config.runUrl || !config.draftId) return;
    $.ajax({
        url: config.runUrl,
        type: 'POST',
        data: { draft_id: config.draftId },
        headers: ajaxHeaders(),
        success(response) {
            if (response && response.html) updateRunCard($root, response.html);
        },
    });
}

function openLatestStyleDemo($root, config, draftId) {
    if (!config.runUrl || !draftId) return;

    $.ajax({
        url: config.runUrl,
        type: 'POST',
        data: { draft_id: draftId },
        headers: ajaxHeaders(),
        success(response) {
            if (response && response.html) updateRunCard($root, response.html);
            const run = response && response.run ? response.run : null;
            const demoUrl = run && run.demo_url ? run.demo_url : '';
            if (demoUrl) {
                window.open(demoUrl, '_blank', 'noopener');
                return;
            }

            setStatusMessage($root, 'Demo is still finishing. Please wait a moment.');
        },
        error(xhr) {
            showCreateError($root, errorMessage(xhr, 'Could not open the style demo.'));
        },
    });
}
function openAiDetailsModal($root, config, draftId) {
    const $modal = $root.find('[data-bb-ai-details-modal]');
    const $body = $root.find('[data-bb-ai-details-body]');
    if (!$modal.length || !config.runDetailsUrl || !draftId) return;

    $body.html('<div class="bb-ai-run-empty">Loading AI run details...</div>');
    $modal.removeClass('bb-hidden').attr('aria-hidden', 'false');

    $.ajax({
        url: config.runDetailsUrl,
        type: 'GET',
        data: { draft_id: draftId },
        headers: ajaxHeaders(),
        success(response) {
            $body.html(response && response.html ? response.html : '<div class="bb-ai-run-empty">No AI details available.</div>');
        },
        error(xhr) {
            $body.html('<div class="bb-ai-run-empty is-danger">' + escapeHtml(errorMessage(xhr, 'Could not load AI details.')) + '</div>');
        },
    });
}

function openReviewPanel($root, config, draftId, continueStep) {
    const $panel = $root.find('[data-bb-ai-review-panel]');
    if (!$panel.length || !config.draftUrl || !draftId) return;

    $panel.removeClass('bb-hidden');
    $panel.data('draftId', draftId);
    $panel.data('continueStep', continueStep || 'draft');
    updateReviewContinueButton($panel, continueStep || 'draft');
    $panel.find('[data-bb-review-meta]').text('Loading generated artifacts...');
    $panel.find('[data-bb-artifact-select]').html('');
    $panel.find('[data-bb-artifact-preview]').text('{}');

    $.ajax({
        url: config.draftUrl,
        type: 'GET',
        data: { draft_id: draftId },
        headers: ajaxHeaders(),
        success(response) {
            const artifacts = Array.isArray(response.artifacts) ? response.artifacts : [];
            const run = response.run || {};
            const resolvedContinueStep = continueStep || nextContinueStep(run);
            $panel.data('artifacts', artifacts);
            $panel.data('continueStep', resolvedContinueStep);
            updateReviewContinueButton($panel, resolvedContinueStep);
            $panel.find('[data-bb-review-meta]').text((run.status || 'draft') + ' - ' + (run.current_step || 'brief'));
            const $select = $panel.find('[data-bb-artifact-select]');
            if (!artifacts.length) {
                $select.append('<option value="draft_payload">draft_payload.json</option>');
                $panel.data('artifacts', [{ filename: 'draft_payload.json', key: 'draft_payload', payload: response.draft || {} }]);
            } else {
                artifacts.forEach(function (artifact, index) {
                    $select.append('<option value="' + index + '">' + escapeHtml(artifact.filename || artifact.key || ('artifact-' + (index + 1))) + '</option>');
                });
            }
            renderSelectedArtifact($panel);
        },
        error(xhr) {
            $panel.find('[data-bb-review-meta]').text(errorMessage(xhr, 'Could not load generated artifacts.'));
        },
    });
}

function updateReviewContinueButton($panel, continueStep) {
    $panel.find('[data-bb-review-continue]').text(reviewContinueLabel(continueStep));
}

function reviewContinueLabel(continueStep) {
    return continueStep === 'style_demo' ? 'Approve style demo' : 'Continue generation';
}

function nextContinueStep(run) {
    const step = String(run && run.current_step ? run.current_step : 'brief');
    const status = String(run && run.status ? run.status : 'draft');
    if (step === 'style_demo_review') return run.has_demo_page ? 'style_demo' : 'draft';
    if (step === 'complexity') return status === 'failed' ? 'style_demo' : 'directions';
    if (step === 'generate_package' || step === 'package_review') return 'package';
    if (step === 'image_review') return 'package';
    if (step === 'completed') return 'template';
    return 'draft';
}

function selectedArtifact($panel) {
    const artifacts = $panel.data('artifacts') || [];
    const index = parseInt($panel.find('[data-bb-artifact-select]').val() || '0', 10) || 0;
    return artifacts[index] || null;
}

function renderSelectedArtifact($panel) {
    const selected = selectedArtifact($panel);
    const payload = selected ? selected.payload : {};
    $panel.find('[data-bb-artifact-preview]').text(JSON.stringify(payload, null, 2));
}

function downloadJson(filename, payload) {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'artifact.json';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(function () {});
        return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
}

function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function errorMessage(xhr, fallback) {
    if (xhr && xhr.responseJSON) {
        if (xhr.responseJSON.message) return xhr.responseJSON.message;
        if (xhr.responseJSON.error) return xhr.responseJSON.error;
    }
    return fallback;
}

// ---- /builder-promo/preview/update : 3-stage refining spinner ----
export function initUpdateStateMachine() {
    const $root = $('[data-bb-update]');
    if (!$root.length) return;

    const taskId = String($root.attr('data-bb-update-task-id') || '').trim();
    const statusUrl = String($root.attr('data-bb-status-url') || '').trim();
    if (taskId && statusUrl) {
        pollPreviewUpdate($root, taskId, statusUrl);
        return;
    }

    let i = 0;
    setProgress($root, updateStages[0].percent);

    const timer = setInterval(function () {
        $root.find('[data-bb-step-item]').eq(i).removeClass('is-active').addClass('is-done');
        i += 1;
        if (i >= updateStages.length) {
            clearInterval(timer);
            const redirect = $root.attr('data-bb-update');
            if (redirect) setTimeout(() => { window.location.href = redirect; }, 700);
            return;
        }
        setProgress($root, updateStages[i].percent);
        $root.find('[data-bb-step-item]').eq(i).addClass('is-active');
    }, 1500);
}

function pollPreviewUpdate($root, taskId, statusUrl) {
    setProgress($root, 34);
    setStepState($root, 0);

    function poll() {
        $.ajax({
            url: statusUrl,
            type: 'POST',
            data: { task_id: taskId },
            headers: ajaxHeaders(),
            success(response) {
                if (!response || !response.success || !response.task) {
                    showCreateError($root, 'Could not read preview update status.');
                    return;
                }

                const task = response.task;
                const status = String(task.status || 'queued');
                const message = task.message || 'Updating your preview...';
                setStatusMessage($root, message);

                if (status === 'failed' || status === 'canceled') {
                    showCreateError($root, task.error || message || 'Preview update stopped.');
                    return;
                }

                if (status === 'completed' || status === 'awaiting_action') {
                    setProgress($root, 100);
                    setStepState($root, 3);
                    const redirect = $root.attr('data-bb-update');
                    if (redirect) setTimeout(() => { window.location.href = redirect; }, 900);
                    return;
                }

                const progressCode = String(task.progress_code || '');
                if (progressCode.indexOf('image') !== -1 || progressCode.indexOf('import') !== -1) {
                    setProgress($root, 82);
                    setStepState($root, 2);
                } else if (status === 'running') {
                    setProgress($root, 62);
                    setStepState($root, 1);
                }

                setTimeout(poll, 2500);
            },
            error(xhr) {
                showCreateError($root, errorMessage(xhr, 'Could not update the preview.'));
            },
        });
    }

    poll();
}

// ---- /builder-promo/checkout build step : assembling progress ----
function runBuildProgress($root) {
    if (!$root.length || $root.data('bbBuildStarted')) return;
    $root.data('bbBuildStarted', true);

    if (!isClickThroughMode() && $root.is('[data-bb-final-build]')) {
        runRealWebsiteBuildProgress($root);
        return;
    }

    runPrototypeBuildProgress($root);
}

function runPrototypeBuildProgress($root) {
    let i = 0;
    setProgress($root, buildSteps[0].percent);

    const timer = setInterval(function () {
        i += 1;
        if (i >= buildSteps.length) {
            clearInterval(timer);
            const target = $root.attr('data-bb-build');
            if (target && target.charAt(0) === '/') {
                setTimeout(() => { window.location.href = target; }, 700);
            } else if (target) {
                setTimeout(() => switchCheckoutStep(target), 700);
            }
            return;
        }
        setProgress($root, buildSteps[i].percent);
        setStepState($root, i);
    }, 1400);
}

function runRealWebsiteBuildProgress($root) {
    const $checkout = $root.closest('[data-bb-checkout]');
    const config = {
        draftId: String($root.attr('data-bb-draft-id') || $checkout.attr('data-bb-draft-id') || '').trim(),
        startUrl: String($checkout.attr('data-bb-build-start-url') || '').trim(),
        statusUrl: String($checkout.attr('data-bb-status-url') || '').trim(),
        continueUrl: String($checkout.attr('data-bb-continue-url') || '').trim(),
        finalizeUrl: String($checkout.attr('data-bb-finalize-url') || '').trim(),
        complexityTier: String($checkout.attr('data-bb-selected-tier') || 'medium').trim(),
        packageLabel: String($checkout.attr('data-bb-selected-package') || 'Complete Website - 5 pages').trim(),
        packageFeedback: String($checkout.attr('data-bb-package-feedback') || '').trim(),
    };

    $root.find('[data-bb-build-package-label]').text(config.packageLabel);
    if (!config.draftId || !config.startUrl || !config.statusUrl || !config.continueUrl) {
        showCreateError($root, 'Website build is missing generation context. Go back to the preview and try again.');
        return;
    }

    renderBuildStage($root, finalBuildStages.directions, 'Starting full website generation...');

    $.ajax({
        url: config.startUrl,
        type: 'POST',
        data: {
            draft_id: config.draftId,
            complexity_tier: config.complexityTier,
            package_feedback: config.packageFeedback,
        },
        headers: ajaxHeaders(),
        success(response) {
            if (!response || !response.success || !response.task || !response.task.id) {
                showCreateError($root, 'Website build did not return a generation task.');
                return;
            }
            pollWebsiteBuildTask($root, config, response.task.id, 'directions');
        },
        error(xhr) {
            showCreateError($root, errorMessage(xhr, 'Could not start full website generation.'));
        },
    });
}

function pollWebsiteBuildTask($root, config, taskId, currentStep) {
    $.ajax({
        url: config.statusUrl,
        type: 'POST',
        data: { task_id: taskId },
        headers: ajaxHeaders(),
        success(response) {
            if (!response || !response.success || !response.task) {
                showCreateError($root, 'Could not read full website generation status.');
                return;
            }

            const task = response.task;
            const step = buildStepFromTask(task) || currentStep;
            renderBuildStage($root, finalBuildStages[step] || finalBuildStages.directions, task.message || null);

            if (task.status === 'failed' || task.status === 'canceled') {
                showCreateError($root, task.error || task.message || 'Website build stopped.');
                return;
            }

            if (task.created_entity) {
                finishWebsiteBuild($root, task);
                return;
            }

            if (task.status === 'completed' || task.status === 'awaiting_action') {
                if (step === 'template') {
                    finishWebsiteBuild($root, task);
                    return;
                }

                continueWebsiteBuild($root, config, step, function (nextTask) {
                    if (!nextTask || !nextTask.id) return;
                    pollWebsiteBuildTask($root, config, nextTask.id, buildStepFromTask(nextTask) || step);
                });
                return;
            }

            setTimeout(function () {
                pollWebsiteBuildTask($root, config, taskId, step);
            }, 2500);
        },
        error(xhr) {
            showCreateError($root, errorMessage(xhr, 'Could not read full website generation status.'));
        },
    });
}

function continueWebsiteBuild($root, config, completedStep, done) {
    setStatusMessage($root, 'Starting the next website build step...');

    $.ajax({
        url: config.continueUrl,
        type: 'POST',
        data: {
            draft_id: config.draftId,
            completed_step: completedStep,
            complexity_tier: config.complexityTier,
            package_feedback: config.packageFeedback,
        },
        headers: ajaxHeaders(),
        success(response) {
            if (!response || !response.success) {
                showCreateError($root, 'Could not continue full website generation.');
                done(null);
                return;
            }

            if (response.done) {
                finishWebsiteBuild($root, { created_entity: response.created_entity || null });
                done(null);
                return;
            }

            if (!response.task || !response.task.id) {
                showCreateError($root, 'Full website generation did not return the next task.');
                done(null);
                return;
            }

            done(response.task);
        },
        error(xhr) {
            showCreateError($root, errorMessage(xhr, 'Could not continue full website generation.'));
            done(null);
        },
    });
}

function buildStepFromTask(task) {
    return {
        generate_directions: 'directions',
        generate_package: 'package',
        skip_pending_images: 'images',
        create_template: 'template',
    }[String(task && task.type ? task.type : '')] || null;
}

function renderBuildStage($root, stage, message) {
    const safeStage = stage || finalBuildStages.directions;
    setProgress($root, safeStage.percent);
    setStatusMessage($root, message || safeStage.message);
    setStepState($root, safeStage.active);
}

function finishWebsiteBuild($root, task) {
    renderBuildStage($root, finalBuildStages.done, finalBuildStages.done.message);
    const run = task && task.run ? task.run : null;
    const entity = (task && task.created_entity) || (run && run.created_entity) || null;
    const demoUrl = (entity && entity.demo_url) || (run && run.demo_url) || '';
    if (demoUrl) $('[data-bb-final-demo-link]').attr('href', demoUrl);
    finalizeWebsiteBuild($root, task, demoUrl);
}

function finalizeWebsiteBuild($root, task, demoUrl) {
    if ($root.data('bbFinalizeStarted')) return;
    $root.data('bbFinalizeStarted', true);

    const $checkout = $root.closest('[data-bb-checkout]');
    const finalizeUrl = String($checkout.attr('data-bb-finalize-url') || '').trim();
    const draftId = String($root.attr('data-bb-draft-id') || $checkout.attr('data-bb-draft-id') || '').trim();
    if (!finalizeUrl || !draftId) {
        setTimeout(function () { switchCheckoutStep('success'); }, 900);
        return;
    }

    setStatusMessage($root, 'Applying the generated website to your business account...');
    $.ajax({
        url: finalizeUrl,
        type: 'POST',
        data: { draft_id: draftId },
        headers: ajaxHeaders(),
        success(response) {
            if (!response || !response.success) {
                $root.data('bbFinalizeStarted', false);
                showCreateError($root, response && response.message ? response.message : 'Website generated, but could not be applied to the organisation.');
                return;
            }
            const websiteUrl = String(response.website_url || demoUrl || '').trim();
            if (websiteUrl) $('[data-bb-final-demo-link]').attr('href', websiteUrl);
            setStatusMessage($root, 'Your website draft is ready.');
            setTimeout(function () { switchCheckoutStep('success'); }, 900);
        },
        error(xhr) {
            $root.data('bbFinalizeStarted', false);
            showCreateError($root, errorMessage(xhr, 'Website generated, but could not be applied to the organisation.'));
        },
    });
}

function switchCheckoutStep(step) {
    $('[data-bb-checkout-step]').addClass('bb-hidden');
    const $target = $('[data-bb-checkout-step="' + step + '"]');
    $target.removeClass('bb-hidden');
    $('html, body').animate({ scrollTop: 0 }, 200);

    if (step === 'payment') initCheckoutStripeCard();
    if (step === 'build') runBuildProgress($target.find('[data-bb-build]'));
}

// ---- /builder-promo/checkout : in-page step switching ----
let checkoutStripe = null;
let checkoutCard = null;

function initCheckoutStripeCard() {
    if (checkoutCard || isClickThroughMode()) return checkoutCard;

    const key = String($('meta[name="stripe-pk"]').attr('content') || '').trim();
    const $error = $('[data-bb-checkout-error]').first();
    if (!key || typeof window.Stripe !== 'function') {
        showCheckoutError($error, 'Secure payment is not configured yet. Please try again later.');
        return null;
    }

    checkoutStripe = window.Stripe(key);
    const rootStyles = window.getComputedStyle(document.documentElement);
    const token = (name, fallback) => String(rootStyles.getPropertyValue(name) || '').trim() || fallback;
    checkoutCard = checkoutStripe.elements().create('card', {
        hidePostalCode: false,
        style: {
            base: {
                color: token('--bb-ink', '#201a16'),
                fontFamily: 'Inter, sans-serif',
                fontSize: '16px',
                '::placeholder': { color: token('--bb-muted', '#8b8178') },
            },
            invalid: { color: token('--bb-danger', '#9f1239') },
        },
    });
    checkoutCard.mount('#bb-builder-promo-card');
    checkoutCard.on('change', (event) => {
        if (event.error) showCheckoutError($error, event.error.message);
        else hideCheckoutError($error);
    });

    return checkoutCard;
}

export function initCheckoutSteps() {
    $(document).on('click', '[data-bb-clickthrough-checkout-next]', function (e) {
        e.preventDefault();
        const order = ['plan', 'payment', 'business', 'build', 'success'];
        const $current = $('[data-bb-checkout-step]').not('.bb-hidden').first();
        const current = String($current.attr('data-bb-checkout-step') || 'plan');
        const index = order.indexOf(current);
        if (current === 'success') {
            const href = $('[data-bb-final-demo-link]').attr('href') || clickThroughUrl('/builder-promo/preview');
            window.location.href = href;
            return;
        }
        switchCheckoutStep(order[Math.min(index + 1, order.length - 1)] || 'plan');
    });

    $(document).on('click', '[data-bb-package-select]', function () {
        const $button = $(this);
        const $checkout = $button.closest('[data-bb-checkout]');
        const tier = String($button.attr('data-bb-package-select') || 'medium');
        const label = String($button.attr('data-bb-package-label') || 'Complete Website - 5 pages');
        const amount = Number($button.attr('data-bb-package-amount') || 30);
        $checkout.attr('data-bb-selected-tier', tier);
        $checkout.attr('data-bb-selected-package', label);
        $checkout.attr('data-bb-selected-amount', amount.toFixed(2));
        $checkout.find('[data-bb-order-package-label], [data-bb-build-package-label]').text(label);
        $checkout.find('[data-bb-order-total]').text('$' + amount.toFixed(2));
        $checkout.find('.bb-plan').removeClass('is-selected');
        $button.closest('.bb-plan').addClass('is-selected');
    });

    $(document).on('click', '[data-bb-checkout-goto]', function (e) {
        e.preventDefault();
        switchCheckoutStep($(this).attr('data-bb-checkout-goto'));
    });

    $(document).on('click', '[data-bb-payment-confirm]', function (e) {
        e.preventDefault();
        if (isClickThroughMode()) {
            switchCheckoutStep('business');
            return;
        }

        const card = initCheckoutStripeCard();
        const $button = $(this);
        const $checkout = $button.closest('[data-bb-checkout]');
        const draftId = String($checkout.attr('data-bb-draft-id') || '').trim();
        const url = String($checkout.attr('data-bb-payment-confirm-url') || '').trim();
        const $error = $checkout.find('[data-bb-checkout-error]');
        if (!draftId || !url) {
            showCheckoutError($error, 'This checkout is missing generation context.');
            return;
        }

        if (!card || !checkoutStripe) {
            showCheckoutError($error, 'Secure card entry could not be loaded.');
            return;
        }
        if (!$checkout.find('[data-bb-website-terms]').is(':checked')) {
            showCheckoutError($error, 'Confirm that BizBaby may build the website using your approved style.');
            return;
        }

        const tier = String($checkout.attr('data-bb-selected-tier') || 'medium');
        const packageLabel = String($checkout.attr('data-bb-selected-package') || 'Complete Website - 5 pages');
        const savePaymentMethod = $checkout.find('[data-bb-save-payment-method]').is(':checked');
        const billing = {};
        $checkout.find('[data-bb-billing-field]').each(function () {
            billing[String($(this).attr('data-bb-billing-field') || '')] = String($(this).val() || '').trim();
        });
        const basePayload = {
            draft_id: draftId,
            complexity_tier: tier,
            package_label: packageLabel,
            save_payment_method: savePaymentMethod ? 1 : 0,
            website_terms_accepted: 1,
        };

        setButtonBusy($button, 'Preparing secure payment...');
        hideCheckoutError($error);
        $.ajax({
            url,
            type: 'POST',
            data: Object.assign({ phase: 'prepare' }, basePayload),
            headers: ajaxHeaders(),
            success(response) {
                if (!response || !response.success) {
                    restoreButton($button);
                    showCheckoutError($error, response && response.message ? response.message : 'Could not confirm package selection.');
                    return;
                }
                if (response.paid) {
                    restoreButton($button);
                    switchCheckoutStep('business');
                    return;
                }

                setButtonBusy($button, 'Confirming card...');
                checkoutStripe.confirmCardPayment(response.client_secret, {
                    payment_method: {
                        card,
                        billing_details: {
                            name: [billing.first_name, billing.last_name].filter(Boolean).join(' '),
                            email: billing.email || undefined,
                            phone: billing.phone || undefined,
                        },
                    },
                }).then((stripeResult) => {
                    if (stripeResult.error) {
                        restoreButton($button);
                        showCheckoutError($error, stripeResult.error.message || 'Stripe could not confirm this card.');
                        return;
                    }

                    setButtonBusy($button, 'Recording payment...');
                    $.ajax({
                        url,
                        type: 'POST',
                        data: Object.assign({
                            phase: 'complete',
                            payment_intent_id: stripeResult.paymentIntent.id,
                        }, basePayload),
                        headers: ajaxHeaders(),
                        success(completeResponse) {
                            restoreButton($button);
                            if (!completeResponse || !completeResponse.success || !completeResponse.paid) {
                                showCheckoutError($error, completeResponse && completeResponse.message ? completeResponse.message : 'Payment could not be recorded.');
                                return;
                            }
                            switchCheckoutStep('business');
                        },
                        error(xhr) {
                            restoreButton($button);
                            showCheckoutError($error, errorMessage(xhr, 'Payment was confirmed but could not be recorded. Please contact BizBaby.'));
                        },
                    });
                });
            },
            error(xhr) {
                restoreButton($button);
                showCheckoutError($error, errorMessage(xhr, 'Could not prepare secure payment.'));
            },
        });
    });

    $(document).on('click', '[data-bb-business-save]', function (e) {
        e.preventDefault();
        if (isClickThroughMode()) {
            switchCheckoutStep('build');
            return;
        }

        const $button = $(this);
        const $checkout = $button.closest('[data-bb-checkout]');
        const draftId = String($checkout.attr('data-bb-draft-id') || '').trim();
        const url = String($checkout.attr('data-bb-business-info-url') || '').trim();
        const $error = $checkout.find('[data-bb-business-error]');
        if (!draftId || !url) {
            showCheckoutError($error, 'This checkout is missing generation context.');
            return;
        }

        const data = { draft_id: draftId };
        $checkout.find('[data-bb-business-field]').each(function () {
            const $field = $(this);
            const key = String($field.attr('data-bb-business-field') || '').trim();
            if (key) data[key] = $field.val();
        });

        setButtonBusy($button, 'Creating workspace...');
        hideCheckoutError($error);
        $.ajax({
            url,
            type: 'POST',
            data,
            headers: ajaxHeaders(),
            success(response) {
                restoreButton($button);
                if (!response || !response.success) {
                    showCheckoutError($error, response && response.message ? response.message : 'Could not save business information.');
                    return;
                }
                switchCheckoutStep('build');
            },
            error(xhr) {
                restoreButton($button);
                showCheckoutError($error, errorMessage(xhr, 'Could not save business information.'));
            },
        });
    });

    const requestedStep = queryParam('checkout_step');
    if (requestedStep) switchCheckoutStep(requestedStep);
}

function showCheckoutError($error, message) {
    $error.removeClass('bb-hidden').text(message || 'Something went wrong. Please try again.');
}

function hideCheckoutError($error) {
    $error.addClass('bb-hidden').text('');
}

function setButtonBusy($button, label) {
    if (!$button.data('bbOriginalHtml')) $button.data('bbOriginalHtml', $button.html());
    $button.prop('disabled', true).text(label);
}

function restoreButton($button) {
    $button.prop('disabled', false).html($button.data('bbOriginalHtml') || $button.html());
}

// ---- /builder-promo/preview : locked <-> unlocked internal states ----
export function initPreviewToggle() {
    if (isClickThroughMode() && queryParam('preview_state') === 'unlocked') {
        $('[data-bb-preview-state]').addClass('bb-hidden');
        $('[data-bb-preview-state="unlocked"]').removeClass('bb-hidden');
    }

    $(document).on('click', '[data-bb-clickthrough-preview-next]', function (e) {
        e.preventDefault();
        const $unlocked = $('[data-bb-preview-state="unlocked"]');
        if ($unlocked.length && $unlocked.hasClass('bb-hidden')) {
            $('[data-bb-preview-state]').addClass('bb-hidden');
            $unlocked.removeClass('bb-hidden');
            $('html, body').animate({ scrollTop: 0 }, 150);
            return;
        }
        window.location.href = clickThroughUrl('/builder-promo/checkout');
    });

    $(document).on('click', '[data-bb-preview-goto]', function (e) {
        e.preventDefault();
        const state = $(this).attr('data-bb-preview-goto');
        $('[data-bb-preview-state]').addClass('bb-hidden');
        $('[data-bb-preview-state="' + state + '"]').removeClass('bb-hidden');
        $('html, body').animate({ scrollTop: 0 }, 150);
    });

    $(document).on('click', '[data-bb-approve-style]', function (e) {
        e.preventDefault();
        const $button = $(this);
        const $preview = $button.closest('[data-bb-preview]');
        const url = String($preview.attr('data-bb-approve-style-url') || '').trim();
        const checkoutUrl = String($preview.attr('data-bb-checkout-url') || '/builder-promo/checkout').trim();
        const draftId = String($preview.attr('data-bb-draft-id') || '').trim();

        if (isClickThroughMode()) {
            window.location.href = clickThroughUrl('/builder-promo/checkout');
            return;
        }

        if (!url || !draftId) {
            window.location.href = checkoutUrl;
            return;
        }

        $button.prop('disabled', true).text('Approving...');
        $.ajax({
            url,
            type: 'POST',
            data: { draft_id: draftId },
            headers: ajaxHeaders(),
            success(response) {
                window.location.href = response && response.redirect ? response.redirect : checkoutUrl;
            },
            error(xhr) {
                window.alert(errorMessage(xhr, 'Could not approve this style.'));
            },
            complete() {
                $button.prop('disabled', false).text('Approve This Style');
            },
        });
    });

    $(document).on('click', '[data-bb-unlock]', function (e) {
        e.preventDefault();
        $('[data-bb-lock]').addClass('bb-hidden');
    });
}

// ---- single-select chip groups ----
export function initChoiceGroups() {
    $(document).on('click', '[data-bb-choice] .bb-chip', function () {
        const $group = $(this).closest('[data-bb-choice]');
        $group.find('.bb-chip').removeClass('is-active');
        $(this).addClass('is-active');
        const $hidden = $group.find('[data-bb-choice-value]');
        if ($hidden.length) $hidden.val($(this).attr('data-value') || '');
    });
}

// ---- password requirements live check ----
export function initPasswordChecks() {
    const $input = $('[data-bb-password]');
    if (!$input.length) return;

    $input.on('input', function () {
        const value = $(this).val();
        toggleReq('len', value.length >= 6);
        toggleReq('num', /\d/.test(value));
        toggleReq('case', /[a-z]/.test(value) && /[A-Z]/.test(value));
    });

    function toggleReq(name, ok) {
        $('[data-req="' + name + '"]').toggleClass('is-ok', ok);
    }
}

// ---- shared helper ----
function isClickThroughMode() {
    return queryParam('clickthrough') === '1';
}

function queryParam(name) {
    try {
        return new URLSearchParams(window.location.search || '').get(name) || '';
    } catch (e) {
        return '';
    }
}

function clickThroughUrl(path, params) {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('clickthrough', '1');
    Object.keys(params || {}).forEach(function (key) {
        url.searchParams.set(key, params[key]);
    });
    return url.toString();
}

function setProgress($scope, pct) {
    const value = Math.max(0, Math.min(100, pct));
    $scope.find('[data-bb-progress-fill]').css('width', value + '%');
    $scope.find('[data-bb-progress-pct]').text(value + '%');
}

function setStepState($scope, activeIndex) {
    $scope.find('[data-bb-step-item]').each(function (idx) {
        const $step = $(this);
        $step.removeClass('is-done is-active');
        if (idx < activeIndex) $step.addClass('is-done');
        else if (idx === activeIndex) $step.addClass('is-active');
    });
}
